import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from '../hooks/use-toast';
import { alertsAPI } from '../services/api';
import { Loader2, ShoppingCart, Check, X } from 'lucide-react';

const CreateAlertModal = ({ open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('query');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [queryLocked, setQueryLocked] = useState(false);
  const [classifyingCategory, setClassifyingCategory] = useState(false);
  const [suggestedCategories, setSuggestedCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [fullClassificationResult, setFullClassificationResult] = useState(null);
  
  // Rate limiting state
  const [lastClassificationTime, setLastClassificationTime] = useState(0);
  const [classificationCount, setClassificationCount] = useState(0);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  
  // Query-based alert
  const [query, setQuery] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [marketplaces, setMarketplaces] = useState(['carousell', 'ebay']); // Both by default
  
  // Advanced search options
  const [exactMatch, setExactMatch] = useState(false);
  const [excludeKeywords, setExcludeKeywords] = useState('');
  const [condition, setCondition] = useState('any');
  
  // URL-based alert
  const [url, setUrl] = useState('');

  const toggleMarketplace = (marketplace) => {
    if (marketplaces.includes(marketplace)) {
      setMarketplaces(marketplaces.filter(m => m !== marketplace));
    } else {
      setMarketplaces([...marketplaces, marketplace]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate input
      if (activeTab === 'query' && !query.trim()) {
        toast({
          title: 'Validation Error',
          description: 'Please enter a search query',
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }

      if (activeTab === 'query' && marketplaces.length === 0) {
        toast({
          title: 'Validation Error',
          description: 'Please select at least one marketplace',
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }

      if (activeTab === 'url' && !url.trim()) {
        toast({
          title: 'Validation Error',
          description: 'Please enter a marketplace URL',
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }

      // Validate classification is complete
      if (activeTab === 'query' && !selectedCategory) {
        toast({
          title: 'Classification Required',
          description: 'Please wait for category classification to complete before creating alert',
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }

      const alertData = activeTab === 'query' 
        ? {
            query: query.trim(),
            from_price: minPrice ? parseFloat(minPrice) : null,
            to_price: maxPrice ? parseFloat(maxPrice) : null,
            marketplaces: marketplaces,
            // Advanced options
            exact_match: exactMatch,
            exclude_keywords: excludeKeywords ? excludeKeywords.split(',').map(k => k.trim()).filter(k => k) : [],
            condition: condition === 'any' ? null : condition,
            // Category classification
            category_id: selectedCategory ? (parseInt(selectedCategory.id) || 0) : null,
            category_path: selectedCategory ? selectedCategory.path : null,
            classification_result: fullClassificationResult,
          }
        : {
            url: url.trim(),
            marketplaces: ['carousell'], // URL-based assumes Carousell
          };

      console.log('Creating alert with data:', alertData);

      await alertsAPI.create(alertData);
      
      toast({
        title: 'Alert Created!',
        description: 'Your alert has been created and will start scraping shortly.',
      });
      
      // Reset form
      setQuery('');
      setMinPrice('');
      setMaxPrice('');
      setUrl('');
      setExactMatch(false);
      setExcludeKeywords('');
      setCondition('any');
      setShowAdvanced(false);
      setQueryLocked(false);
      setSuggestedCategories([]);
      setSelectedCategory(null);
      setFullClassificationResult(null);
      
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error('Alert creation error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to create alert';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">Create New Alert</DialogTitle>
          <p className="text-gray-600 text-sm mt-2">
            Get notified when new items matching your criteria are found on marketplace sites
          </p>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 pr-2">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="query">Search Query</TabsTrigger>
            <TabsTrigger value="url">Direct URL</TabsTrigger>
          </TabsList>

          <form onSubmit={handleSubmit}>
            <TabsContent value="query" className="space-y-4 mt-4">
              <div>
                <Label htmlFor="query">Search Query *</Label>
                <div className="relative mt-2">
                  <Input
                    id="query"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="e.g. iPhone 14 Pro"
                    required={activeTab === 'query'}
                    disabled={queryLocked}
                    className={queryLocked ? 'pr-10 bg-gray-100 text-gray-700' : 'pr-10'}
                  />
                  <button
                    type="button"
                    onClick={async (e) => {
                      // Prevent multiple clicks
                      if (classifyingCategory) return;
                      
                      e.preventDefault();
                      e.stopPropagation();
                      
                      const newLockState = !queryLocked;
                      
                      // If locking, check rate limits
                      if (newLockState && query.trim()) {
                        const now = Date.now();
                        
                        // Check if user is in cooldown period
                        if (now < cooldownUntil) {
                          const remainingSeconds = Math.ceil((cooldownUntil - now) / 1000);
                          toast({
                            title: 'Rate Limit',
                            description: `Please wait ${remainingSeconds} seconds before classifying again.`,
                            variant: 'destructive'
                          });
                          return;
                        }
                        
                        // Basic rate limiting: max 5 classifications per minute
                        const timeSinceLastClassification = now - lastClassificationTime;
                        if (timeSinceLastClassification < 60000) {
                          // Within 1 minute - increment count
                          const newCount = classificationCount + 1;
                          
                          if (newCount > 5) {
                            // Exceeded limit - impose 30 second cooldown
                            const cooldownEnd = now + 30000;
                            setCooldownUntil(cooldownEnd);
                            toast({
                              title: 'Rate Limit Exceeded',
                              description: 'Too many classification requests. Please wait 30 seconds.',
                              variant: 'destructive'
                            });
                            return;
                          }
                          
                          setClassificationCount(newCount);
                        } else {
                          // More than 1 minute - reset counter
                          setClassificationCount(1);
                        }
                        
                        setLastClassificationTime(now);
                      }
                      
                      setQueryLocked(newLockState);
                      
                      // If locking, classify the category
                      if (newLockState && query.trim()) {
                        setClassifyingCategory(true);
                        setSuggestedCategories([]);
                        setSelectedCategory(null);
                        
                        try {
                          // Call through backend proxy, not direct to localhost:5001
                          const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
                          const response = await axios.post(`${BACKEND_URL}/api/classify/product`, {
                            title: query,
                            description: ''
                          });
                          
                          // Store full classification result
                          setFullClassificationResult({
                            ...response.data,
                            classified_at: new Date().toISOString(),
                            query: query.trim()
                          });
                          
                          // Build suggestions from response
                          const suggestions = [];
                          
                          // Add main result
                          if (response.data.category_id) {
                            suggestions.push({
                              id: response.data.category_id,
                              path: response.data.category_path,
                              confidence: response.data.confidence
                            });
                          }
                          
                          // Add alternatives
                          if (response.data.alternatives) {
                            response.data.alternatives.forEach(alt => {
                              suggestions.push({
                                id: alt.id,
                                path: alt.path,
                                confidence: alt.score
                              });
                            });
                          }
                          
                          // Filter out low confidence (<10%) or unclassified
                          const filtered = suggestions.filter(s => 
                            s.confidence > 0.1 && s.path !== 'Unclassified'
                          );
                          
                          if (filtered.length > 0) {
                            setSuggestedCategories(filtered);
                            setSelectedCategory(filtered[0]); // Auto-select best
                          } else {
                            // Fallback if all low confidence
                            setSuggestedCategories(suggestions);
                            setSelectedCategory(suggestions[0]);
                          }
                        } catch (error) {
                          console.error('Category classification failed:', error);
                          toast({
                            title: 'Classification Failed',
                            description: 'Could not classify category. Using fallback.',
                            variant: 'destructive'
                          });
                          // Set fallback
                          const fallback = {id: '0', path: 'Unclassified', confidence: 0};
                          setSuggestedCategories([fallback]);
                          setSelectedCategory(fallback);
                        } finally {
                          setClassifyingCategory(false);
                        }
                      } else if (!newLockState) {
                        // Unlocking - clear categories
                        setSuggestedCategories([]);
                        setSelectedCategory(null);
                        setFullClassificationResult(null);
                      }
                    }}
                    disabled={classifyingCategory}
                    className={`absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md transition-colors ${
                      classifyingCategory 
                        ? 'bg-gray-300 cursor-wait' 
                        : 'hover:bg-gray-200 cursor-pointer active:scale-95'
                    }`}
                    title={classifyingCategory ? 'Classifying...' : queryLocked ? 'Click to unlock and edit' : 'Click to lock query'}
                  >
                    {classifyingCategory ? (
                      <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
                    ) : queryLocked ? (
                      <X className="h-4 w-4 text-red-500" />
                    ) : (
                      <Check className="h-4 w-4 text-green-600" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  What product are you looking for?
                  {queryLocked && (
                    <span className="ml-2 text-green-600 font-medium">✓ Locked</span>
                  )}
                </p>
              </div>

              {/* Category Selection */}
              <div>
                <Label htmlFor="category">Product Category *</Label>
                <Input
                  id="category"
                  value={classifyingCategory ? 'Classifying...' : (selectedCategory ? selectedCategory.path : '')}
                  disabled={true}
                  placeholder="Lock query to auto-classify..."
                  className="mt-2 bg-gray-100 text-gray-700"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {classifyingCategory ? (
                    <span className="text-blue-600">
                      <Loader2 className="inline h-3 w-3 mr-1 animate-spin" />
                      Classifying category with AI... (may take 10-30 seconds)
                    </span>
                  ) : selectedCategory ? (
                    <span className="text-green-600">
                      ✓ Category auto-detected (confidence: {(selectedCategory.confidence * 100).toFixed(0)}%)
                    </span>
                  ) : (
                    'Category will be auto-classified when you lock the query'
                  )}
                </p>
                
                {/* Radio button selection for suggested categories */}
                {suggestedCategories.length > 0 && (
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
                    <p className="text-sm font-medium text-blue-900">Suggested Categories:</p>
                    {suggestedCategories.map((cat, idx) => (
                      <label
                        key={cat.id}
                        className="flex items-center gap-2 cursor-pointer hover:bg-blue-100 p-2 rounded"
                      >
                        <input
                          type="radio"
                          name="category"
                          checked={selectedCategory?.id === cat.id}
                          onChange={() => setSelectedCategory(cat)}
                          className="w-4 h-4 text-blue-600"
                        />
                        <div className="flex-1">
                          <p className="text-sm text-gray-900">{cat.path}</p>
                          <p className="text-xs text-gray-500">
                            Confidence: {(cat.confidence * 100).toFixed(1)}%
                            {idx === 0 && ' (Recommended)'}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="minPrice">Min Price (S$)</Label>
                  <Input
                    id="minPrice"
                    type="number"
                    value={minPrice}
                    onChange={(e) => setMinPrice(e.target.value)}
                    placeholder="0"
                    min="0"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="maxPrice">Max Price (S$)</Label>
                  <Input
                    id="maxPrice"
                    type="number"
                    value={maxPrice}
                    onChange={(e) => setMaxPrice(e.target.value)}
                    placeholder="10000"
                    min="0"
                    className="mt-2"
                  />
                </div>
              </div>

              {/* Marketplace Selection */}
              <div>
                <Label className="mb-3 block">Marketplaces to Search</Label>
                <div className="space-y-3 bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="carousell"
                      checked={marketplaces.includes('carousell')}
                      onCheckedChange={() => toggleMarketplace('carousell')}
                    />
                    <label
                      htmlFor="carousell"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center cursor-pointer"
                    >
                      <ShoppingCart className="w-4 h-4 mr-2 text-red-500" />
                      Carousell Singapore
                    </label>
                  </div>

                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="ebay"
                      checked={marketplaces.includes('ebay')}
                      onCheckedChange={() => toggleMarketplace('ebay')}
                    />
                    <label
                      htmlFor="ebay"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center cursor-pointer"
                    >
                      <ShoppingCart className="w-4 h-4 mr-2 text-blue-500" />
                      eBay Singapore
                    </label>
                  </div>

                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="shopee"
                      checked={marketplaces.includes('shopee')}
                      onCheckedChange={() => toggleMarketplace('shopee')}
                    />
                    <label
                      htmlFor="shopee"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center cursor-pointer"
                    >
                      <ShoppingCart className="w-4 h-4 mr-2 text-orange-500" />
                      Shopee Singapore
                    </label>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Select marketplaces to search. More sources = better coverage!
                </p>
              </div>

              {/* Advanced Search Options */}
              <div className="border-t pt-4">
                <button
                  type="button"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="text-sm font-medium text-gray-700 hover:text-gray-900 flex items-center gap-2 w-full"
                >
                  <span className="text-lg">{showAdvanced ? '▼' : '▶'}</span>
                  Advanced Search Options (Precise Matching)
                </button>
                
                {showAdvanced && (
                  <div className="mt-4 space-y-4 bg-purple-50 border border-purple-200 rounded-lg p-4">
                    {/* Exact Match Toggle */}
                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="exactMatch"
                        checked={exactMatch}
                        onCheckedChange={setExactMatch}
                      />
                      <div className="flex-1">
                        <label
                          htmlFor="exactMatch"
                          className="text-sm font-medium leading-none cursor-pointer"
                        >
                          🎯 Exact Match Only
                        </label>
                        <p className="text-xs text-gray-600 mt-1">
                          Only show listings that contain the exact search phrase. Example: &quot;iPhone 11&quot; won&apos;t show &quot;iPhone 13&quot;
                        </p>
                      </div>
                    </div>

                    {/* Exclude Keywords */}
                    <div>
                      <Label htmlFor="excludeKeywords" className="text-sm">
                        ❌ Exclude Keywords (comma-separated)
                      </Label>
                      <Input
                        id="excludeKeywords"
                        value={excludeKeywords}
                        onChange={(e) => setExcludeKeywords(e.target.value)}
                        placeholder="e.g. iPhone 13, iPhone 12, iPad"
                        className="mt-2"
                      />
                      <p className="text-xs text-gray-600 mt-1">
                        Filter out listings containing these words. Great for excluding other models!
                      </p>
                    </div>

                    {/* Condition Filter */}
                    <div>
                      <Label htmlFor="condition" className="text-sm">
                        📦 Item Condition
                      </Label>
                      <select
                        id="condition"
                        value={condition}
                        onChange={(e) => setCondition(e.target.value)}
                        className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                      >
                        <option value="any">Any Condition</option>
                        <option value="new">Brand New Only</option>
                        <option value="used">Used Only</option>
                        <option value="refurbished">Refurbished Only</option>
                      </select>
                      <p className="text-xs text-gray-600 mt-1">
                        Filter by item condition
                      </p>
                    </div>

                    <div className="bg-purple-100 rounded p-2 text-xs text-purple-800">
                      <strong>💡 Example:</strong> Search &quot;iPhone 11&quot;, enable exact match, exclude &quot;iPhone 13, iPhone 12, iPad&quot; → Only get iPhone 11 results!
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  💡 <strong>Tip:</strong> Use Advanced Options for precise searches. Leave price fields empty to search all prices
                </p>
              </div>
            </TabsContent>

            <TabsContent value="url" className="space-y-4 mt-4">
              <div>
                <Label htmlFor="url">Marketplace URL *</Label>
                <Input
                  id="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://www.carousell.sg/..."
                  required={activeTab === 'url'}
                  className="mt-2"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Paste a marketplace search URL or category URL
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  💡 <strong>Tip:</strong> Browse the marketplace, apply filters, then copy the URL
                </p>
              </div>
            </TabsContent>

            <div className="flex space-x-3 mt-6">
              <Button
                type="submit"
                disabled={loading || (activeTab === 'query' && (!queryLocked || !selectedCategory))}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : classifyingCategory ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Classifying...
                  </>
                ) : (
                  'Create Alert'
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </Button>
            </div>
            {activeTab === 'query' && !queryLocked && query.trim() && (
              <p className="text-sm text-amber-600 mt-2 text-center">
                ⚠️ Click the checkmark (✓) to confirm your search query first
              </p>
            )}
            {activeTab === 'query' && queryLocked && !selectedCategory && !classifyingCategory && (
              <p className="text-sm text-red-600 mt-2 text-center">
                ❌ Classification failed. Please try a different search query.
              </p>
            )}
            {activeTab === 'query' && classifyingCategory && (
              <p className="text-sm text-blue-600 mt-2 text-center">
                🔄 Classifying product category... Please wait.
              </p>
            )}
          </form>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateAlertModal;
